/**
 * Diff 工具模块
 * 基于 LCS 的行级 + 词级 diff 算法，用于 prompt 对比展示
 */

export interface DiffSegment {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
}

export interface DiffLine {
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  text: string;
  segments: DiffSegment[];
}

export interface SideBySideDiff {
  leftLines: DiffLine[];
  rightLines: DiffLine[];
}

/**
 * LCS 算法：计算两个数组的最长公共子序列
 */
function lcs<T>(a: T[], b: T[]): T[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // 回溯获取 LCS
  const result: T[] = [];
  let i = m,
    j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }

  return result;
}

/**
 * 基于 LCS 计算两个字符串数组之间的 diff 操作序列
 */
function computeOperations(
  oldArr: string[],
  newArr: string[]
): Array<{ type: 'keep' | 'remove' | 'add'; value: string }> {
  const common = lcs(oldArr, newArr);
  const ops: Array<{ type: 'keep' | 'remove' | 'add'; value: string }> = [];

  let oi = 0,
    ni = 0,
    ci = 0;

  while (ci < common.length) {
    // 输出 old 中跳过的行
    while (oi < oldArr.length && oldArr[oi] !== common[ci]) {
      ops.push({ type: 'remove', value: oldArr[oi] });
      oi++;
    }
    // 输出 new 中跳过的行
    while (ni < newArr.length && newArr[ni] !== common[ci]) {
      ops.push({ type: 'add', value: newArr[ni] });
      ni++;
    }
    // 公共元素
    ops.push({ type: 'keep', value: common[ci] });
    oi++;
    ni++;
    ci++;
  }

  // 剩余尾部
  while (oi < oldArr.length) {
    ops.push({ type: 'remove', value: oldArr[oi++] });
  }
  while (ni < newArr.length) {
    ops.push({ type: 'add', value: newArr[ni++] });
  }

  return ops;
}

/**
 * 词级 diff：将一行文本拆分为词，比较变更细节
 */
function computeWordDiff(
  oldLine: string,
  newLine: string
): { leftSegments: DiffSegment[]; rightSegments: DiffSegment[] } {
  const oldWords = oldLine.split(/(\s+)/);
  const newWords = newLine.split(/(\s+)/);
  const ops = computeOperations(oldWords, newWords);

  const leftSegments: DiffSegment[] = [];
  const rightSegments: DiffSegment[] = [];

  for (const op of ops) {
    if (op.type === 'keep') {
      leftSegments.push({ type: 'unchanged', text: op.value });
      rightSegments.push({ type: 'unchanged', text: op.value });
    } else if (op.type === 'remove') {
      leftSegments.push({ type: 'removed', text: op.value });
    } else {
      rightSegments.push({ type: 'added', text: op.value });
    }
  }

  return { leftSegments, rightSegments };
}

/**
 * 计算两段文本的 side-by-side diff
 */
export function computeSideBySideDiff(oldText: string, newText: string): SideBySideDiff {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const ops = computeOperations(oldLines, newLines);

  const leftLines: DiffLine[] = [];
  const rightLines: DiffLine[] = [];

  let i = 0;
  while (i < ops.length) {
    const op = ops[i];

    if (op.type === 'keep') {
      const line: DiffLine = {
        type: 'unchanged',
        text: op.value,
        segments: [{ type: 'unchanged', text: op.value }],
      };
      leftLines.push(line);
      rightLines.push({ ...line, segments: [...line.segments] });
      i++;
    } else if (op.type === 'remove') {
      // 检查是否后面紧跟一个 add，构成 modified 行
      if (i + 1 < ops.length && ops[i + 1].type === 'add') {
        const { leftSegments, rightSegments } = computeWordDiff(op.value, ops[i + 1].value);
        leftLines.push({ type: 'modified', text: op.value, segments: leftSegments });
        rightLines.push({ type: 'modified', text: ops[i + 1].value, segments: rightSegments });
        i += 2;
      } else {
        leftLines.push({
          type: 'removed',
          text: op.value,
          segments: [{ type: 'removed', text: op.value }],
        });
        rightLines.push({
          type: 'removed',
          text: '',
          segments: [],
        });
        i++;
      }
    } else {
      // add
      leftLines.push({
        type: 'added',
        text: '',
        segments: [],
      });
      rightLines.push({
        type: 'added',
        text: op.value,
        segments: [{ type: 'added', text: op.value }],
      });
      i++;
    }
  }

  return { leftLines, rightLines };
}

/**
 * 带行号的 diff 行
 */
export interface NumberedDiffLine {
  type: 'added' | 'removed' | 'modified' | 'unchanged';
  leftLineNo: number | null;
  rightLineNo: number | null;
  leftSegments: DiffSegment[];
  rightSegments: DiffSegment[];
  leftText: string;
  rightText: string;
}

/**
 * 计算带行号的 side-by-side diff
 */
export function computeNumberedSideBySideDiff(oldText: string, newText: string): NumberedDiffLine[] {
  const { leftLines, rightLines } = computeSideBySideDiff(oldText, newText);
  const result: NumberedDiffLine[] = [];

  let leftNo = 0;
  let rightNo = 0;

  for (let i = 0; i < leftLines.length; i++) {
    const left = leftLines[i];
    const right = rightLines[i];

    const hasLeft = left.segments.length > 0;
    const hasRight = right.segments.length > 0;

    if (hasLeft) leftNo++;
    if (hasRight) rightNo++;

    result.push({
      type:
        left.type === 'unchanged'
          ? 'unchanged'
          : right.type === 'added' && !hasLeft
          ? 'added'
          : left.type === 'removed' && !hasRight
          ? 'removed'
          : 'modified',
      leftLineNo: hasLeft ? leftNo : null,
      rightLineNo: hasRight ? rightNo : null,
      leftSegments: left.segments,
      rightSegments: right.segments,
      leftText: left.text,
      rightText: right.text,
    });
  }

  return result;
}
